import {inject, Injectable, Signal, signal, WritableSignal} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {CovToastService} from '@cov/ui-angular';
import {translate} from '@jsverse/transloco';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  map,
  Observable,
  of,
  OperatorFunction,
  share,
  switchMap,
  take,
  tap,
  throwError,
  withLatestFrom,
} from 'rxjs';
import {ListRequest} from '../../../interfaces/list-request.interface';
import {MimeType} from '../../../shared/enum/mime-type.enum';
import {SidePanelService} from '../../../shared/services/side-panel.service';
import {downloadFile} from '../../../shared/utils/file-download';
import {getExtensionByMimeType} from '../../../shared/utils/file.utils';
import {INQUIRY_NOT_ACCESSIBLE_ERROR} from '../const/inquiry-api.const';
import {InquiryApiClient} from '../data-access/api-client/inquiries.api-client';
import {InquirySidePanelDataInterface} from '../data-access/interface/inquiry-sidepanel-data.interface';
import {
  AllInquiryResponse,
  CreateGeneralInquiry,
  CreateOrderInquiry,
  CustomerSalesRepresentativeInfo,
  InquiryEvent,
  InquiryOrder,
} from '../data-access/interface/inquiry.interface';
import {PageId} from '../enum/inquiry-page.enum';
import {InquirySideDrawerComponent} from '../ui/inquiry-side-drawer/inquiry-side-drawer.component';

@Injectable({
  providedIn: 'root',
})
export class InquiryService {
  // INJECTS
  private sidePanelService = inject(SidePanelService);
  private toastService: CovToastService = inject(CovToastService);
  private apiClient: InquiryApiClient = inject(InquiryApiClient);

  // STATE
  private _order: WritableSignal<InquiryOrder | null> = signal(null);
  public isOpen: WritableSignal<boolean> = signal(false);

  // Loading
  public allInquiriesListLoading: WritableSignal<boolean> = signal(false);
  public inquiryDetailLoading: WritableSignal<boolean> = signal(false);
  public attachmentExportLoading: WritableSignal<boolean> = signal(false);
  public inquirySubmitLoading: WritableSignal<boolean> = signal(false);

  private allInquiriesLoadTrigger$ = new BehaviorSubject<ListRequest | null>(null);
  private allInquiries$ = this.allInquiriesLoadTrigger$.pipe(
    filter(Boolean),
    switchMap((request: ListRequest) => {
      this.allInquiriesListLoading.set(true);
      return this.apiClient.getAllInquiries(request).pipe(
        tap(() => this.allInquiriesListLoading.set(false)),
        catchError(() => {
          this.allInquiriesListLoading.set(false);
          return throwError(() => new Error('Inquiry list could not be loaded'));
        }),
      );
    }),
    share(),
  );

  protected attachmentExportTrigger$ = new BehaviorSubject<{
    url: string;
    name: string;
    mimeType: string;
    ticketId: string;
  }>({url: '', name: '', mimeType: '', ticketId: ''});
  protected exportAttachment$ = this.attachmentExportTrigger$.pipe(
    filter(Boolean),
    withLatestFrom(toObservable(this.attachmentExportLoading)),
    filter(([_, isLoading]) => !isLoading), // skip if download is in progress
    switchMap(
      ([{url, name, mimeType, ticketId}]: [
        {url: string; name: string; mimeType: string; ticketId: string},
        boolean,
      ]) => {
        this.attachmentExportLoading.set(true);
        return this.apiClient.exportAttachment(url, name, mimeType, ticketId).pipe(
          tap((response: Blob) => {
            const extension = getExtensionByMimeType(mimeType as MimeType);
            if (!extension) {
              throwError(() => new Error('MIME type is not available'));
              return;
            }
            downloadFile(response, name, mimeType, extension);
          }),
          catchError(() => {
            this.toastService.error(translate('invoices.common.actions.export.error'));
            return of(null);
          }),
          finalize(() => this.attachmentExportLoading.set(false)),
        );
      },
    ),
    share(),
  );

  public csrInformation: WritableSignal<CustomerSalesRepresentativeInfo[]> = signal([]);

  public loadAllInquiries(listRequest: ListRequest): Observable<AllInquiryResponse> {
    this.allInquiriesLoadTrigger$.next(listRequest);
    return this.allInquiries$;
  }

  public open(data: InquirySidePanelDataInterface = {page: PageId.INTRO}): void {
    if (this.isOpen()) {
      return;
    }
    this.isOpen.set(true);
    this.sidePanelService
      .showComponent<InquirySideDrawerComponent, InquirySidePanelDataInterface, void>(
        InquirySideDrawerComponent,
        data,
      )
      .onClose.pipe(
        tap(() => this.isOpen.set(false)),
        take(1),
      )
      .subscribe();
  }

  public setOrder(order: InquiryOrder): void {
    this._order.set(order);
  }

  public getInquiryDetail(ticketId: string): Observable<InquiryEvent[] | any> {
    this.inquiryDetailLoading.set(true);
    return this.apiClient.getInquiryDetail(ticketId).pipe(
      map((response) => {
        // Check if response is an error message (200 with error body)
        if ((response as any)?.message === INQUIRY_NOT_ACCESSIBLE_ERROR) {
          return response;
        }
        return response.events ?? [];
      }),
      tap(() => this.inquiryDetailLoading.set(false)),
      catchError(() => {
        this.inquiryDetailLoading.set(false);
        return throwError(() => new Error('Inquiry details could not be loaded'));
      }),
    );
  }

  public get order(): Signal<InquiryOrder | null> {
    return this._order.asReadonly();
  }

  public clear(): void {
    this._order.set(null);
  }

  public loadCsrInformation() {
    return this.apiClient
      .getCsrInformation()
      .pipe(tap((result: CustomerSalesRepresentativeInfo[]) => this.csrInformation.set(result)));
  }

  public createGeneralInquiry(message: string, csrEmail: string, soldTo: string): Observable<void> {
    const request = this.createGeneralOrderInquiryData(`${message}`, csrEmail, soldTo);
    this.inquirySubmitLoading.set(true);
    return this.apiClient.createGeneralInquiry(request).pipe(this.handleSuccessAndError<void>());
  }

  public createOrderInquiry(
    order: InquiryOrder,
    message: string,
    files?: File[],
  ): Observable<void> {
    const request = {
      ...this.createOrderInquiryData(order),
      message,
      files,
    } as CreateOrderInquiry;
    this.inquirySubmitLoading.set(true);
    return this.apiClient.createOrderInquiry(request).pipe(this.handleSuccessAndError<void>());
  }

  private handleSuccessAndError<T>(): OperatorFunction<T, T> {
    return (source$) =>
      source$.pipe(
        tap(() => {
          this.inquirySubmitLoading.set(false);
          this.toastService.success(
            translate('inquiries.sideDrawer.contactPanel.notifications.inquiryCreated'),
          );
        }),
        catchError(() => {
          this.inquirySubmitLoading.set(false);
          this.toastService.error(
            translate('inquiries.sideDrawer.contactPanel.notifications.inquiryCreatedError'),
          );
          return of();
        }),
      );
  }

  private createGeneralOrderInquiryData(
    message: string,
    csrEmail: string,
    soldTo: string,
  ): CreateGeneralInquiry {
    return {
      soldTo,
      message,
      csrEmail,
    };
  }

  private createOrderInquiryData(order: InquiryOrder): Partial<CreateOrderInquiry> {
    return {
      orderNumber: order?.orderId ?? '',
      shipTo: order?.shipTo?.sapCustomerID,
      soldTo: order?.soldTo?.sapCustomerID,
      poNumber: order?.poNumber,
      orderPosition: order?.orderItemPosition,
      product: {
        code: order?.productCode ?? '',
        name: order?.productName ?? '',
      },
    };
  }

  public exportAttachment(url: string, name: string, mimeType: string, ticketId: string): void {
    this.attachmentExportTrigger$.next({url, name, mimeType, ticketId});
    this.exportAttachment$.pipe(take(1)).subscribe();
  }
}
